import { Eye, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserRole } from "@/hooks/useUserRole";
import { useTierOverride } from "@/hooks/useTierOverride";
import { useWorkspace } from "@/hooks/useWorkspace";
import type { Tier } from "@/lib/tier";
import { cn } from "@/lib/utils";

const PREVIEWABLE_TIERS: { value: Tier; label: string }[] = [
  { value: "starter", label: "Starter" },
  { value: "plus", label: "Plus" },
  { value: "pro", label: "Pro" },
  { value: "elite", label: "Elite" },
  { value: "agency", label: "Agency" },
];

const tierLabel = (t: Tier | null) => {
  if (!t) return "—";
  const found = PREVIEWABLE_TIERS.find(x => x.value === t);
  if (found) return found.label;
  if (t === "comp") return "Comp";
  if (t === "admin_workspace") return "Admin";
  if (t === "super_admin") return "Super Admin";
  return t;
};

export const TierPreviewSwitcher = () => {
  const { isSuperAdmin, loading } = useUserRole();
  const { override, setOverride, clearOverride } = useTierOverride();
  const { realPrimaryTier } = useWorkspace();

  if (loading || !isSuperAdmin) return null;

  const realTier: Tier | null = realPrimaryTier;

  const isOverridden = override !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant={isOverridden ? "default" : "outline"}
          className={cn(
            "h-7 gap-1.5 rounded-full px-3 text-xs font-medium",
            isOverridden && "bg-accent text-accent-foreground hover:bg-accent/90",
          )}
        >
          <Eye className="h-3.5 w-3.5" />
          {isOverridden ? (
            <>
              <span>Viewing as: {tierLabel(override)}</span>
              <X
                className="h-3.5 w-3.5 opacity-80 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  clearOverride();
                }}
              />
            </>
          ) : (
            <span>View as: {tierLabel(realTier)}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Super admin tier preview
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => clearOverride()}>
          <span className="flex-1">Real tier ({tierLabel(realTier)})</span>
          {!isOverridden && <Check className="h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {PREVIEWABLE_TIERS.map(t => (
          <DropdownMenuItem key={t.value} onSelect={() => setOverride(t.value)}>
            <span className="flex-1">{t.label}</span>
            {override === t.value && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
